import { useSearchParams } from "react-router-dom";
import { useDaycareHotelAccess } from "@/hooks/useDaycareHotelAccess";
import type { StayShowFilter } from "./schedule/scheduleModel";

type StoredShowFilter = "all" | "day_care" | "hotel";

const STORAGE_KEY = "daycare-hotel:show";

function readStored(): StayShowFilter | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY) as StoredShowFilter | null;
    return value === "all" ? "all" : value === "day_care" ? "daycare" : value === "hotel" ? "hotel" : null;
  } catch {
    return null;
  }
}

function store(value: StayShowFilter) {
  try {
    window.localStorage.setItem(STORAGE_KEY, value === "daycare" ? "day_care" : value);
  } catch {
    // Ignore unavailable localStorage.
  }
}

/**
 * The Tudo/Creche/Hotel filter, shared by the page header and every tab.
 * A clinic with only one of the two modules is pinned to it.
 */
export function useStayShowFilter() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { mode } = useDaycareHotelAccess();
  const queryShow = searchParams.get("show");

  const show: StayShowFilter =
    mode === "daycare" ? "daycare"
      : mode === "hotel" ? "hotel"
      : queryShow === "daycare" || queryShow === "hotel" || queryShow === "all" ? queryShow
      : readStored() ?? "all";

  const setShow = (value: string) => {
    if (value !== "all" && value !== "daycare" && value !== "hotel") return;
    store(value);
    const next = new URLSearchParams(searchParams);
    next.set("show", value);
    setSearchParams(next, { replace: true });
  };

  return { show, setShow, canFilter: mode === "both" };
}
